import * as THREE from 'three';
import { Water } from 'three/addons/objects/Water.js';
import { buildGerstnerGLSL, type WaveComponent } from '../../shared/waveField';
import { createWaterNormalTexture } from '../utils/waterNormals';
import { SUN_DIRECTION } from './SkySystem';
import type { SeaStateConfig } from './SeaState';

export interface OceanOptions {
  mobile: boolean;
  /** Seed for the procedural normal map. */
  normalSeed?: number;
}

/** Scene-graph name used by `StormFx` to drive the lightning flash uniform. */
export const OCEAN_WATER_NAME = 'ocean-water';

/**
 * World-space noise for crest breakup and micro-chop. Value noise drives the
 * foam (rotated + non-integer octaves so it never reads as a repeating lattice)
 * while smooth gradient noise drives the chop so the surface normal has no
 * value-noise terrace steps. All hashes are deterministic — no texture lookups
 * and no `Math.random`.
 */
const WATER_NOISE_GLSL = /* glsl */ `
float waterHash21( vec2 p ) {
  p = fract( p * vec2( 123.34, 345.45 ) );
  p += dot( p, p + 34.345 );
  return fract( p.x * p.y );
}

float waterValueNoise( vec2 p ) {
  vec2 i = floor( p );
  vec2 f = fract( p );
  f = f * f * ( 3.0 - 2.0 * f );
  float a = waterHash21( i );
  float b = waterHash21( i + vec2( 1.0, 0.0 ) );
  float c = waterHash21( i + vec2( 0.0, 1.0 ) );
  float d = waterHash21( i + vec2( 1.0, 1.0 ) );
  return mix( mix( a, b, f.x ), mix( c, d, f.x ), f.y );
}

vec2 waterHash22( vec2 p ) {
  vec3 p3 = fract( vec3( p.xyx ) * 0.1031 );
  p3 += dot( p3, p3.yzx + 33.33 );
  return fract( ( p3.xx + p3.yz ) * p3.zy ) * 2.0 - 1.0;
}

float waterGradNoise( vec2 p ) {
  vec2 i = floor( p );
  vec2 f = fract( p );
  vec2 u = f * f * ( 3.0 - 2.0 * f );
  vec2 a = waterHash22( i );
  vec2 b = waterHash22( i + vec2( 1.0, 0.0 ) );
  vec2 c = waterHash22( i + vec2( 0.0, 1.0 ) );
  vec2 d = waterHash22( i + vec2( 1.0, 1.0 ) );
  return mix(
    mix( dot( a, f - vec2( 0.0, 0.0 ) ), dot( b, f - vec2( 1.0, 0.0 ) ), u.x ),
    mix( dot( c, f - vec2( 0.0, 1.0 ) ), dot( d, f - vec2( 1.0, 1.0 ) ), u.x ),
    u.y );
}

float waterFoamNoise( vec2 p ) {
  vec2 r = vec2( p.x * 0.8660 - p.y * 0.5000, p.x * 0.5000 + p.y * 0.8660 );
  return waterValueNoise( r * 2.0 ) * 0.5
    + waterValueNoise( r * 4.37 ) * 0.25
    + waterValueNoise( r * 8.91 ) * 0.25;
}
`;

/**
 * Ocean surface, sun and fill lighting. The sky dome now lives in
 * `SkySystem`; this system only owns the water, its reflections and the
 * directional/ambient light.
 *
 * The water reuses `Water.js` for the reflection pass and injects the shared
 * Gerstner displacement/slope (built from the room's wave table) through
 * `onBeforeCompile`, so GPU waves match the CPU field exactly. If the expected
 * shader anchors are missing the patch is skipped and the reflective fallback
 * keeps rendering.
 */
export class OceanSystem {
  readonly sunLight: THREE.DirectionalLight;
  readonly hemisphere: THREE.HemisphereLight;
  readonly water: Water;

  private readonly normalTexture: THREE.CanvasTexture;
  private readonly waveAmp = { value: 1 };
  private readonly waveTime = { value: 0 };
  private readonly foam = { value: 1 };
  private readonly whitecap = { value: 0 };
  private readonly windDir = { value: new THREE.Vector2(1, 0) };
  private readonly windStreak = { value: 0 };
  private readonly storm = { value: 0 };
  private readonly flash = { value: 0 };
  private readonly chop = { value: 0.35 };
  private readonly skyReflection = { value: 0.55 };
  private readonly foamFadeNear = { value: 46 };
  private readonly foamFadeFar = { value: 240 };
  private readonly detailFadeNear = { value: 65 };
  private readonly detailFadeFar = { value: 360 };
  private readonly subsurface = { value: 0.4 };
  private readonly subsurfaceTint = { value: new THREE.Color('#0f5b6b') };
  private readonly shallowTint = { value: new THREE.Color('#0c7688') };
  private readonly deepTint = { value: new THREE.Color('#04101a') };
  private readonly extinction = { value: new THREE.Vector3(0.36, 0.13, 0.04) };
  private readonly reflectionTint = { value: new THREE.Color('#7e93a6') };
  private readonly sunGlint = { value: 0.85 };
  private readonly planeSize: number;
  private readonly cellSize: number;
  private waveTable: WaveComponent[];
  private glsl: string;
  private tableKey: string;
  private patched = false;
  private foamWarned = false;

  constructor(
    private readonly scene: THREE.Scene,
    waveTable: WaveComponent[],
    options: OceanOptions,
  ) {
    this.waveTable = waveTable;
    this.glsl = buildGerstnerGLSL(waveTable);
    this.tableKey = keyFor(waveTable);

    const mobile = options.mobile;
    this.planeSize = mobile ? 420 : 760;
    const segments = mobile ? 96 : 176;
    this.cellSize = this.planeSize / segments;

    // Aerial-perspective fog: the near range keeps the ship crisp while the far
    // range melts the horizon into the sky dome's horizon colour. `StormFx`
    // pulls both in and deepens the colour as the storm builds.
    this.scene.fog = new THREE.Fog(new THREE.Color('#8fa4b4'), 120, 900);

    // --- lighting ---
    this.hemisphere = new THREE.HemisphereLight('#cddced', '#1d3846', 1.35);
    this.scene.add(this.hemisphere);

    this.sunLight = new THREE.DirectionalLight('#ffefd8', 3.0);
    this.sunLight.position.copy(SUN_DIRECTION).multiplyScalar(120);
    this.sunLight.castShadow = true;
    this.sunLight.shadow.mapSize.set(mobile ? 2048 : 4096, mobile ? 2048 : 4096);
    // Fit the ortho frustum to the ship plus its on-water shadow (the mast
    // casts ~29 units at this sun angle), while tightening the old ±55 box so
    // rigging resolves to more than a single texel.
    const shadowHalf = 45;
    this.sunLight.shadow.camera.near = 70;
    this.sunLight.shadow.camera.far = 180;
    this.sunLight.shadow.camera.left = -shadowHalf;
    this.sunLight.shadow.camera.right = shadowHalf;
    this.sunLight.shadow.camera.top = shadowHalf;
    this.sunLight.shadow.camera.bottom = -shadowHalf;
    this.sunLight.shadow.bias = -0.0003;
    this.sunLight.shadow.normalBias = 0.05;
    this.sunLight.shadow.radius = 2;
    this.scene.add(this.sunLight);
    this.scene.add(this.sunLight.target);

    // --- water ---
    this.normalTexture = createWaterNormalTexture(mobile ? 192 : 256, options.normalSeed ?? 1337, 1);
    const geometry = new THREE.PlaneGeometry(this.planeSize, this.planeSize, segments, segments);
    this.water = new Water(geometry, {
      textureWidth: mobile ? 512 : 1024,
      textureHeight: mobile ? 512 : 1024,
      waterNormals: this.normalTexture,
      sunDirection: SUN_DIRECTION.clone().normalize(),
      sunColor: 0xe8f0ff,
      waterColor: 0x0a3d46,
      distortionScale: mobile ? 4.0 : 6.0,
      fog: true,
    });
    this.water.rotation.x = -Math.PI / 2;
    this.water.receiveShadow = true;
    this.water.material.uniforms.uWaveAmp = this.waveAmp;
    this.water.material.uniforms.uWaveTime = this.waveTime;
    this.water.material.uniforms.uFoam = this.foam;
    this.water.material.uniforms.uWhitecap = this.whitecap;
    this.water.material.uniforms.uWindDir = this.windDir;
    this.water.material.uniforms.uWindStreak = this.windStreak;
    this.water.material.uniforms.uStorm = this.storm;
    this.water.material.uniforms.uFlash = this.flash;
    this.water.material.uniforms.uChop = this.chop;
    this.water.material.uniforms.uSkyReflection = this.skyReflection;
    this.water.material.uniforms.uFoamFadeNear = this.foamFadeNear;
    this.water.material.uniforms.uFoamFadeFar = this.foamFadeFar;
    this.water.material.uniforms.uDetailFadeNear = this.detailFadeNear;
    this.water.material.uniforms.uDetailFadeFar = this.detailFadeFar;
    this.water.material.uniforms.uSubsurface = this.subsurface;
    this.water.material.uniforms.uSubsurfaceTint = this.subsurfaceTint;
    this.water.material.uniforms.uShallowTint = this.shallowTint;
    this.water.material.uniforms.uDeepTint = this.deepTint;
    this.water.material.uniforms.uExtinction = this.extinction;
    this.water.material.uniforms.uReflectionTint = this.reflectionTint;
    this.water.material.uniforms.uSunGlint = this.sunGlint;
    // Wind glitter is aligned with the sun's horizontal azimuth so the streak
    // reads as a sun-path on the water regardless of the live wind direction.
    this.windDir.value.set(SUN_DIRECTION.x, SUN_DIRECTION.z).normalize();
    this.water.name = OCEAN_WATER_NAME;
    // Normal-detail scale: higher = finer chop in the reflection normal map.
    this.water.material.uniforms.size.value = mobile ? 3.5 : 4.5;
    this.water.material.customProgramCacheKey = () => `gerstner-${this.tableKey}`;
    this.water.material.onBeforeCompile = (shader) => this.patchShader(shader);
    this.scene.add(this.water);

    // Prime the shadow camera toward the origin so the first frames are shaded.
    this.sunLight.target.position.set(0, 0, 0);
    this.sunLight.target.updateMatrixWorld();
  }

  /** Replace the active wave table (called when the room's table arrives). */
  setWaveTable(waveTable: WaveComponent[]): void {
    const key = keyFor(waveTable);
    if (key === this.tableKey) return;
    this.waveTable = waveTable;
    this.glsl = buildGerstnerGLSL(waveTable);
    this.tableKey = key;
    this.water.material.needsUpdate = true;
  }

  get activeWaveTable(): WaveComponent[] {
    return this.waveTable;
  }

  setWaveAmplitude(amplitude: number): void {
    this.waveAmp.value = amplitude;
  }

  /**
   * Apply a sea-state configuration: visual wave height, crest foam and
   * whitecaps. The shared Gerstner table and the deterministic wave-field
   * contract are unchanged — only the `uWaveAmp`/`uFoam`/`uWhitecap` uniforms
   * move.
   */
  setSeaState(config: SeaStateConfig): void {
    this.waveAmp.value = config.amplitude;
    this.foam.value = config.foam;
    this.whitecap.value = config.whitecap;
    this.windStreak.value = config.windStreak ?? 0;
    this.storm.value = config.storm ?? 0;
    this.chop.value = config.chop ?? 0.35;
    this.skyReflection.value = config.skyReflection ?? 0.55;
    this.subsurface.value = config.subsurface ?? 0.4;
  }

  /**
   * Advance the ocean and keep it centred on the ship.
   * @param waveTime  Already-scaled wave time (seconds).
   * @param shipPosition  World position of the ship.
   */
  update(waveTime: number, shipPosition: THREE.Vector3): void {
    this.waveTime.value = waveTime;
    this.water.material.uniforms.time.value = waveTime;

    this.water.position.set(
      Math.round(shipPosition.x / this.cellSize) * this.cellSize,
      0,
      Math.round(shipPosition.z / this.cellSize) * this.cellSize,
    );

    this.sunLight.position.copy(shipPosition).addScaledVector(SUN_DIRECTION, 120);
    this.sunLight.target.position.copy(shipPosition);
    this.sunLight.target.updateMatrixWorld();

    // Keep the horizon fog anchored to the camera-relative world, not the ship.
    const fog = this.scene.fog as THREE.Fog | null;
    if (fog) {
      fog.near = this.planeSize * 0.14;
      fog.far = this.planeSize * 1.15;
    }
  }

  private patchShader(shader: THREE.WebGLProgramParametersWithUniforms): void {
    const vertexAnchor =
      'mirrorCoord = modelMatrix * vec4( position, 1.0 );\n\t\t\t\t\tworldPosition = mirrorCoord.xyzw;\n\t\t\t\t\tmirrorCoord = textureMatrix * mirrorCoord;\n\t\t\t\t\tvec4 mvPosition =  modelViewMatrix * vec4( position, 1.0 );';
    const fragmentAnchor =
      'vec3 surfaceNormal = normalize( noise.xzy * vec3( 1.5, 1.0, 1.5 ) );';

    if (!shader.vertexShader.includes('mirrorCoord = modelMatrix * vec4( position, 1.0 )') ||
        !shader.fragmentShader.includes(fragmentAnchor)) {
      if (!this.patched) {
        console.warn('[OceanSystem] Water shader anchors not found; using flat reflective fallback.');
      }
      return;
    }

    const vertexReplacement = [
      'vec4 waveOrigin = modelMatrix * vec4( position, 1.0 );',
      'vec3 waveWorld = waveOrigin.xyz + waveDisp( waveOrigin.xz, uWaveTime );',
      'vWaveHeight = waveWorld.y;',
      'vec2 crestSlope = waveSlope( waveOrigin.xz, uWaveTime );',
      'vWaveSteepness = length( crestSlope );',
      'vWaveNormal = normalize( vec3( -crestSlope.x, 1.0, -crestSlope.y ) );',
      'worldPosition = vec4( waveWorld, 1.0 );',
      'mirrorCoord = textureMatrix * worldPosition;',
      'vec4 mvPosition = viewMatrix * vec4( waveWorld, 1.0 );',
    ].join('\n\t\t\t\t\t');

    if (!shader.vertexShader.includes(vertexAnchor)) {
      // Whitespace differs across three revisions; fall back to a line-based swap.
      shader.vertexShader = shader.vertexShader
        .replace(
          'mirrorCoord = modelMatrix * vec4( position, 1.0 );',
          'vec4 waveOrigin = modelMatrix * vec4( position, 1.0 );\n\t\t\t\t\tvec3 waveWorld = waveOrigin.xyz + waveDisp( waveOrigin.xz, uWaveTime );\n\t\t\t\t\tvWaveHeight = waveWorld.y;\n\t\t\t\t\tvec2 crestSlope = waveSlope( waveOrigin.xz, uWaveTime );\n\t\t\t\t\tvWaveSteepness = length( crestSlope );\n\t\t\t\t\tvWaveNormal = normalize( vec3( -crestSlope.x, 1.0, -crestSlope.y ) );\n\t\t\t\t\tworldPosition = vec4( waveWorld, 1.0 );\n\t\t\t\t\tmirrorCoord = textureMatrix * worldPosition;',
        )
        .replace(
          'vec4 mvPosition =  modelViewMatrix * vec4( position, 1.0 );',
          'vec4 mvPosition = viewMatrix * vec4( waveWorld, 1.0 );',
        );
    } else {
      shader.vertexShader = shader.vertexShader.replace(vertexAnchor, vertexReplacement);
    }

    const seaUniformDecls =
      'uniform float uWaveTime;\n' +
      'uniform float uFoam;\n' +
      'uniform float uWhitecap;\n' +
      'uniform float uWindStreak;\n' +
      'uniform float uStorm;\n' +
      'uniform float uFlash;\n' +
      'uniform float uChop;\n' +
      'uniform float uSkyReflection;\n' +
      'uniform float uFoamFadeNear;\n' +
      'uniform float uFoamFadeFar;\n' +
      'uniform float uDetailFadeNear;\n' +
      'uniform float uDetailFadeFar;\n' +
      'uniform float uSubsurface;\n' +
      'uniform float uSunGlint;\n' +
      'uniform vec3 uSubsurfaceTint;\n' +
      'uniform vec3 uShallowTint;\n' +
      'uniform vec3 uDeepTint;\n' +
      'uniform vec3 uExtinction;\n' +
      'uniform vec3 uReflectionTint;\n' +
      'uniform vec2 uWindDir;\n' +
      'varying float vWaveHeight;\n' +
      'varying float vWaveSteepness;\n' +
      'varying vec3 vWaveNormal;\n';
    shader.vertexShader = `${seaUniformDecls}${this.glsl}\n` + shader.vertexShader;

    shader.fragmentShader = shader.fragmentShader.replace(
      fragmentAnchor,
      [
        'vec3 gerstnerNormal = normalize( vWaveNormal );',
        'vec3 microNormal = normalize( noise.xzy * vec3( 1.5, 1.0, 1.5 ) );',
        'vec3 viewDir = normalize( eye - worldPosition.xyz );',
        'float viewDistance = length( eye - worldPosition.xyz );',
        // Distance fade on high-frequency micro normals to cleanly kill horizon specular aliasing and banding
        'float normalFade = 1.0 - smoothstep( uDetailFadeNear, uDetailFadeFar, viewDistance );',
        // Dual scrolling procedural detail normal layers with irrational rotation angles to break tiling
        'vec2 pDetail1 = worldPosition.xz * 0.22 + vec2( uWaveTime * 0.041, uWaveTime * 0.027 );',
        'vec2 pDetail2 = vec2( worldPosition.x * 0.8660 - worldPosition.z * 0.5000, worldPosition.x * 0.5000 + worldPosition.z * 0.8660 ) * 0.37 - vec2( uWaveTime * 0.031, -uWaveTime * 0.038 );',
        'float dN1 = waterGradNoise( pDetail1 * 3.5 );',
        'float dN2 = waterGradNoise( pDetail2 * 4.9 );',
        'vec3 dualDetail = normalize( vec3( -dN1 * 0.35 - dN2 * 0.25, 1.0, -dN1 * 0.25 + dN2 * 0.35 ) );',
        'vec3 combinedDetail = normalize( microNormal * 0.55 + dualDetail * 0.45 );',
        'vec3 surfaceNormal = normalize( gerstnerNormal + combinedDetail * ( 0.20 * normalFade ) );',
        'vec2 chopAxis = normalize( uWindDir + vec2( 1e-4 ) );',
        'float chopDetail = 1.0 - smoothstep( 35.0, 180.0, viewDistance );',
        'vec2 chopP = ( worldPosition.xz * 2.8 + chopAxis * uWaveTime * 2.0 ) * ( 0.55 + 0.45 * chopDetail );',
        'chopP = vec2( chopP.x + chopP.y, chopP.x - chopP.y ) * 0.7071;',
        'float chopNoise = waterGradNoise( chopP ) * 0.7 + waterGradNoise( chopP * 2.3 ) * 0.3;',
        'vec3 chopNormal = normalize( vec3( -chopNoise * chopAxis.x, 0.0, -chopNoise * chopAxis.y ) + vec3( 0.0, 1.0, 0.0 ) );',
        'surfaceNormal = normalize( surfaceNormal + chopNormal * ( uChop * normalFade * ( 0.12 + 0.35 * uStorm ) ) );',
        // Large-scale detail octave that fades in toward the horizon only: it
        // breaks the normal map's far-field tiling and the horizon band without
        // adding geometry or touching the shared wave table. GPU Gems 1 ch.1
        // edge-length filtering (wavelengths too short for the tessellation).
        'float farDetail = smoothstep( 70.0, 260.0, viewDistance );',
        'vec2 farP = worldPosition.xz * 0.07 + chopAxis * uWaveTime * 0.35;',
        'float farNoise = waterGradNoise( farP ) * 0.8 + waterGradNoise( farP * 1.9 ) * 0.2;',
        'vec3 farNormal = normalize( vec3( -farNoise * 0.35, 0.0, -farNoise * 0.35 ) + vec3( 0.0, 1.0, 0.0 ) );',
        'surfaceNormal = normalize( surfaceNormal + farNormal * farDetail * 0.12 );',
        'float waveSteepness = vWaveSteepness;',
        // Analytical steepness + wave height peak detection (Jacobian breaking crest)
        'float crestBreak = smoothstep( 0.28, 0.75, waveSteepness ) * smoothstep( 0.02, 0.60, vWaveHeight );',
        'float foamBase = smoothstep( 0.18, 0.52, waveSteepness );',
        'float foamCrest = crestBreak * 1.25;',
        'float whitecapCrest = smoothstep( 0.52, 0.95, waveSteepness ) * smoothstep( 0.15, 0.85, vWaveHeight );',
        'float foamFade = 1.0 - smoothstep( uFoamFadeNear, uFoamFadeFar, viewDistance );',
        // Anisotropic foam coordinates stretched along the wind/chop axis so
        // crest foam reads as wind-blown streaks, not round blobs (the normal-
        // detail idea from GPU Gems 1 ch.1: fine detail lives in the normal/noise
        // field, not the geometry). The three octaves are de-correlated in time
        // and frequency so the crest never repeats as a lattice.
        'vec2 foamAlong = vec2( dot( worldPosition.xz, chopAxis ), dot( worldPosition.xz, vec2( -chopAxis.y, chopAxis.x ) ) );',
        'float crestNoise = waterFoamNoise( vec2( foamAlong.x * 0.55, foamAlong.y * 1.6 ) + chopAxis * uWaveTime * 0.9 );',
        'float foamStreak = waterValueNoise( vec2( foamAlong.x * 1.9, foamAlong.y * 4.1 ) - chopAxis * uWaveTime * 1.7 + 13.7 );',
        'float foamGrain = waterValueNoise( worldPosition.xz * 11.3 + chopAxis * uWaveTime * 3.1 + 5.3 );',
        'float foamPattern = crestNoise * 0.5 + foamStreak * 0.3 + foamGrain * 0.2;',
        'float foamFactor = clamp( foamBase * uFoam * 0.45 + foamCrest * uFoam * 0.85 + whitecapCrest * uWhitecap, 0.0, 1.0 );',
        'foamFactor *= foamFade;',
        'foamFactor *= mix( 0.55, 1.3, foamPattern );',
        'foamFactor = clamp( foamFactor, 0.0, 1.0 );',
      ].join('\n\t\t\t\t\t'),
    );

    // Foam/crest tinting: brighten steep wave crests with a pale foam colour.
    const albedoAnchor =
      'vec3 albedo = mix( ( sunColor * diffuseLight * 0.3 + scatter ) * getShadowMask(), reflectionSample + specularLight, reflectance );';
    if (shader.fragmentShader.includes(albedoAnchor)) {
      shader.fragmentShader = shader.fragmentShader.replace(
        albedoAnchor,
        [
          'vec3 specular = specularLight * ( 1.0 + uStorm * 0.55 );',
          'vec2 windAxis = normalize( uWindDir + vec2( 1e-4 ) );',
          // High-frequency sun glitter gated by analytic wave slope and uWindStreak
          'float sunFacet = max( dot( normalize( noise.xz ), windAxis ), 0.0 );',
          'float sparkleBand = pow( sunFacet, 26.0 );',
          'float glitterNoise = waterFoamNoise( worldPosition.xz * 12.0 - windAxis * uWaveTime * 1.8 );',
          'float sparkleMask = pow( glitterNoise, 7.0 );',
          'float crestGlitter = smoothstep( 0.45, 0.88, vWaveSteepness );',
          'float glitter = sparkleBand * sparkleMask * crestGlitter * uWindStreak;',
          'specular += sunColor * glitter * 1.9;',
          // Sun micro-facet glint / sparkle
          'vec3 halfVec = normalize( normalize( sunDirection ) + viewDir );',
          'float NdotH = max( 0.0, dot( surfaceNormal, halfVec ) );',
          'float glintNoise = waterHash21( floor( worldPosition.xz * 12.0 + uWaveTime * 4.0 ) );',
          'float sunSparkle = pow( NdotH, 120.0 ) * smoothstep( 0.65, 1.0, glintNoise ) * uSunGlint * ( 1.0 - uStorm * 0.85 );',
          'specular += sunSparkle * sunColor * 1.6;',
          'specular = mix( specular, specular * vec3( 0.72, 0.86, 1.12 ), uStorm * 0.6 );',
          'float crestLight = smoothstep( 0.02, 0.7, vWaveHeight );',
          'float troughDark = 1.0 - smoothstep( -0.9, 0.06, vWaveHeight );',
          'float facing = max( 0.0, dot( surfaceNormal, viewDir ) );',
          // Schlick Fresnel approximation for water (F0 = 0.02)
          'float fresnelSchlick = 0.02 + 0.98 * pow( 1.0 - facing, 5.0 );',
          // Beer-Lambert exponential depth absorption
          'float optDepth = clamp( 2.2 - vWaveHeight * 1.6 + 0.8 * ( 1.0 - facing ), 0.25, 7.5 );',
          'vec3 transmission = exp( -uExtinction * optDepth );',
          'vec3 beerLambertWater = mix( uDeepTint, uShallowTint, transmission );',
          // Subsurface crest scattering: light penetrates thin backlit wave crests
          'vec3 lightDir = normalize( sunDirection );',
          'float sssDot = max( 0.0, dot( -lightDir, viewDir ) );',
          'float sssIntensity = pow( sssDot, 3.0 ) * crestLight * ( 1.0 - uStorm * 0.75 );',
          'vec3 sssColor = uSubsurfaceTint * ( 0.6 + 1.2 * sssIntensity );',
          'vec3 baseWater = mix( beerLambertWater, uDeepTint, 0.25 + 0.75 * uStorm );',
          'vec3 waterBody = mix( baseWater, sssColor, uSubsurface * ( 0.30 + 0.50 * crestLight + 0.20 * sssIntensity ) );',
          'float troughShadow = 1.0 - waveSteepness * 0.45;',
          // Weight the planar mirror sample toward the sky tint
          'float skyTint = clamp( 0.2 + 0.4 * uSkyReflection + 0.12 * uStorm, 0.0, 0.8 );',
          'vec3 stormSky = mix( uReflectionTint, uReflectionTint * vec3( 0.55, 0.66, 0.82 ), uStorm );',
          'vec3 reflectedSky = mix( reflectionSample, stormSky, skyTint );',
          'float skyWeight = 0.75 + 0.4 * uSkyReflection;',
          'vec3 skyReflection = reflectedSky * skyWeight;',
          'vec3 scatterCol = waterBody * ( 1.0 - 0.35 * uSkyReflection ) * troughShadow;',
          'float effectiveReflectance = mix( reflectance, fresnelSchlick, 0.65 );',
          'vec3 albedo = mix( ( sunColor * diffuseLight * 0.3 + scatterCol ) * getShadowMask(), skyReflection + specular, effectiveReflectance );',
          'albedo *= mix( 1.0, 0.68, troughDark * uStorm );',
          'albedo = mix( albedo, albedo * vec3( 0.72, 0.88, 1.0 ) + vec3( 0.02, 0.06, 0.09 ), crestLight * uStorm * 0.28 );',
          // Foam tone varies with anisotropic grain
          'float foamTone = mix( 0.62, 1.28, foamGrain * 0.55 + foamStreak * 0.45 );',
          'vec3 foamColor = vec3( 0.82, 0.91, 0.98 ) * foamTone * ( 0.75 + 0.25 * dot( surfaceNormal, vec3( 0.0, 1.0, 0.0 ) ) );',
          'foamColor = mix( foamColor, foamColor * vec3( 0.9, 0.96, 1.06 ), foamStreak * 0.55 );',
          'albedo = mix( albedo, foamColor, foamFactor * ( 0.6 + 0.32 * uStorm ) );',
          'albedo += uFlash * ( 0.18 + 0.15 * effectiveReflectance ) * vec3( 0.9, 0.95, 1.1 );',
        ].join('\n\t\t\t\t\t'),
      );
    } else if (!this.foamWarned) {
      this.foamWarned = true;
      console.warn('[OceanSystem] Water albedo anchor not found; skipping foam tint.');
    }

    shader.fragmentShader = `${seaUniformDecls}${this.glsl}${WATER_NOISE_GLSL}\n` + shader.fragmentShader;

    this.patched = true;
  }

  dispose(): void {
    this.scene.remove(this.water);
    this.scene.remove(this.sunLight);
    this.scene.remove(this.sunLight.target);
    this.scene.remove(this.hemisphere);
    this.water.geometry.dispose();
    // Water.js owns an internal reflection render target; release its texture.
    const mirrorSampler = this.water.material.uniforms.mirrorSampler.value as THREE.Texture | null;
    if (mirrorSampler && mirrorSampler.isTexture) mirrorSampler.dispose();
    this.water.material.dispose();
    this.normalTexture.dispose();
    this.scene.fog = null;
  }
}

function keyFor(table: WaveComponent[]): string {
  return table
    .map((component) => `${component.wavelength}:${component.amplitude.toFixed(4)}`)
    .join('|');
}
